import React, { useEffect, useState } from "react";
import { getItem } from "../../../utils/localStorage";
import { useNavigate } from "react-router-dom";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { residentUnitsURL, getLandlordIncomeURL, getCustomerInvoicesURL, getWalletBalanceURL, getWalletHistoryURL, requestWithdrawalURL } from "../../../utils/urls";
import Layout from "../component/layout";

function Dashboard() {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState({
    totalIncome: 0,
    totalPaid: 0,
    totalBalance: 0
  });
  const [expenseSummary, setExpenseSummary] = useState({
    totalToPay: 0,
    totalPaid: 0,
    totalUnpaid: 0,
    totalOverdue: 0
  });

  // Wallet state
  const [walletBalance, setWalletBalance] = useState(0);
  const [recentWithdrawals, setRecentWithdrawals] = useState(0);
  const [recentTransactions, setRecentTransactions] = useState([]);

  // Modal states
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalNotes, setWithdrawalNotes] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const fetchResidentUnits = async () => {
    try {
      let CustomerId = await getItem("selectedCustomerId");
      let FacilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${residentUnitsURL}/${CustomerId}/${FacilityId}`, "GET", {});
      if (response.success) {
        setUnits(response.data);
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      toastify(err.message, "error");
    }
  };

  const fetchRevenueSummary = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const homeOwnerId = await getItem('selectedCustomerId');
      const response = await makeRequest2(`${getLandlordIncomeURL}/${facilityId}/${homeOwnerId}`, 'GET', {});
      if (response.success && response.data) {
        setRevenueSummary({
          totalIncome: response.data.totalIncome || 0,
          totalPaid: response.data.totalPaid || 0,
          totalBalance: response.data.totalBalance || 0
        });
      }
    } catch (err) {
      console.warn("Revenue summary fetch failed:", err.message);
    }
  };

  const fetchExpenseSummary = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const customerId = await getItem('selectedCustomerId');
      const response = await makeRequest2(
        `${getCustomerInvoicesURL}/${facilityId}/${customerId}`,
        "GET",
        {}
      );
      if (response.success && response.data?.data) {
        const fetchedInvoices = response.data.data.invoices || [];
        const contractInvoices = fetchedInvoices.filter(
          (invoice) => invoice.whatFor && invoice.whatFor.invoiceType === 'Contract'
        );
        const totalToPay = contractInvoices.reduce((acc, inv) => acc + (inv.totalAmount || 0), 0);
        const totalPaid = contractInvoices.reduce(
          (acc, inv) => acc + (inv.status === 'Paid' ? inv.totalAmount || 0 : 0), 0
        );
        const totalUnpaid = contractInvoices.reduce(
          (acc, inv) => acc + (inv.status === 'Unpaid' || inv.status === 'Pending' ? inv.totalAmount || 0 : 0), 0
        );
        const totalOverdue = contractInvoices.reduce(
          (acc, inv) => acc + (inv.status === 'Overdue' ? inv.totalAmount || 0 : 0), 0
        );
        setExpenseSummary({ totalToPay, totalPaid, totalUnpaid, totalOverdue });
      }
    } catch (err) {
      console.warn("Expense summary fetch failed:", err.message);
    }
  };

  // Wallet functions

  const fetchWalletData = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const customerId = await getItem('selectedCustomerId');

      if (!facilityId || !customerId) return;

      // Fetch balance
      const balanceResponse = await makeRequest2(
        `${getWalletBalanceURL}/${facilityId}/${customerId}`,
        'GET',
        {}
      );

      console.log("WALLET BALANCE RESPONSE", balanceResponse);

      if (balanceResponse.success) {
        // Handle nested response: response.data.data.balance
        const balance = balanceResponse.data?.data?.balance ||
          balanceResponse.data?.balance ||
          0;
        setWalletBalance(balance);
        console.log("✅ Balance set to:", balance);
      }

      // Fetch history
      const historyResponse = await makeRequest2(
        `${getWalletHistoryURL}/${facilityId}/${customerId}`,
        'GET',
        {}
      );

      console.log("WALLET HISTORY RESPONSE", historyResponse);

      if (historyResponse.success) {
        // Handle nested response: response.data.data
        let transactions = historyResponse.data?.data || historyResponse.data || [];

        // Ensure it's an array
        if (!Array.isArray(transactions)) {
          transactions = [];
        }

        console.log("✅ Transactions found:", transactions.length);

        setRecentTransactions(transactions.slice(0, 5));

        // Calculate withdrawals in the last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const recentWithdrawalsTotal = transactions
          .filter(t =>
            t.type === 'debit' &&
            t.source === 'withdrawal-request' &&
            new Date(t.createdAt) >= thirtyDaysAgo &&
            t.status === 'completed'
          )
          .reduce((sum, t) => sum + t.amount, 0);

        setRecentWithdrawals(recentWithdrawalsTotal);
        console.log("✅ Recent withdrawals (30d):", recentWithdrawalsTotal);
      }
    } catch (err) {
      console.warn("Wallet data fetch failed:", err.message);
    }
  };

  // Handle withdrawal request
  const handleWithdraw = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const customerId = await getItem('selectedCustomerId');

      if (!facilityId || !customerId) {
        toastify('Facility or Customer not selected', 'error');
        return;
      }

      const amount = parseFloat(withdrawalAmount);
      if (!amount || amount <= 0) {
        toastify('Please enter a valid amount', 'error');
        return;
      }

      if (amount > walletBalance) {
        toastify('Insufficient balance', 'error');
        return;
      }

      setWithdrawing(true);

      const response = await makeRequest2(
        `${requestWithdrawalURL}/${facilityId}/${customerId}`,
        'POST',
        {
          amount,
          notes: withdrawalNotes || 'Withdrawal request'
        }
      );

      if (response.success) {
        toastify('Withdrawal request submitted successfully', 'success');
        setShowWithdrawModal(false);
        setWithdrawalAmount('');
        setWithdrawalNotes('');
        // Refresh wallet data
        await fetchWalletData();
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      toastify(err.message || 'Failed to process withdrawal', 'error');
    } finally {
      setWithdrawing(false);
    }
  };

  const occupiedUnits = units.filter(unit => unit.tenantId && unit.residentId);
  const nonOccupiedUnits = units.filter(unit => !(unit.tenantId && unit.residentId));

  useEffect(() => {
    const checkStorageAndFetch = async () => {
      const CustomerId = await getItem("selectedCustomerId");
      const FacilityId = await getItem("selectedFacilityId");
      if (CustomerId && FacilityId) {
        fetchResidentUnits();
        await Promise.all([
          fetchRevenueSummary(),
          fetchExpenseSummary(),
          fetchWalletData()
        ]);
      }
    };
    checkStorageAndFetch();
  }, []);

  const formatCurrency = (amount) => {
    if (typeof amount !== 'number') return 'KES 0.00';
    return `KES ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const netPosition = (revenueSummary.totalIncome || 0) - (expenseSummary.totalToPay || 0);

  // Helper components
  const StatCard = ({ label, value, icon, color, bgColor }) => (
    <div className="col-md-3 col-6">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body p-3">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <p className="text-muted small mb-1">{label}</p>
              <h4 className={`fw-bold mb-0 ${color}`}>{value}</h4>
            </div>
            <div className={`${bgColor} rounded-circle p-2`}>
              <i className={`ti ti-${icon} ${color}`} style={{ fontSize: '20px' }}></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const UnitCard = ({ unit, status, borderColor, badgeColor }) => (
    <div className="col-md-3 col-6">
      <div
        className={`card h-100 border border-${borderColor} border-opacity-25`}
        style={{
          cursor: "pointer",
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}
        onClick={() => navigate(`/portal/occupancy_reports/view_unit/${unit._id}`)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h6 className="fw-bold mb-0">{unit.name}</h6>
              <p className="text-muted small mb-0">{unit.unitType}</p>
            </div>
            <span className={`badge bg-${badgeColor}`}>{unit.customerType}</span>
          </div>
          <div className="d-flex align-items-center gap-2 mt-2">
            <i className={`ti ti-${status === 'Occupied' ? 'user' : 'user-off'} text-muted`} style={{ fontSize: '14px' }}></i>
            <span className="small text-muted">{status}</span>
            <i className={`ti ti-circle-filled text-${status === 'Occupied' ? 'success' : 'secondary'} ms-auto`} style={{ fontSize: '8px' }}></i>
          </div>
        </div>
      </div>
    </div>
  );

  const SectionHeader = ({ icon, title, count, color, subtitle }) => (
    <div className="d-flex align-items-center justify-content-between mb-3">
      <div>
        <h6 className="fw-bold mb-0">
          <i className={`ti ti-${icon} text-${color} me-2`}></i>
          {title}
          <span className={`badge bg-${color} ms-2`}>{count}</span>
        </h6>
        <p className="text-muted small mb-0">{subtitle}</p>
      </div>
    </div>
  );

  const FinanceMetric = ({ label, value, color, bgColor }) => (
    <div className={`${bgColor} rounded p-2 text-center`}>
      <p className="text-muted small mb-0">{label}</p>
      <p className={`fw-bold text-${color} mb-0`}>{formatCurrency(value)}</p>
    </div>
  );

  const FinanceCard = ({ title, icon, color, total, metrics, onClick }) => (
    <div className="col-md-6">
      <div
        className="p-3 rounded-3 h-100"
        style={{
          border: '1px solid #e5e7eb',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={onClick}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = color === 'primary' ? '#3b82f6' : '#6b7280'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
      >
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className={`bg-${color} bg-opacity-10 rounded-circle p-2`}>
              <i className={`ti ti-${icon} text-${color}`} style={{ fontSize: '18px' }}></i>
            </div>
            <h6 className="mb-0 fw-semibold">{title}</h6>
          </div>
          <i className="ti ti-chevron-right text-muted" />
        </div>
        <h4 className={`fw-bold text-${color} mb-3`}>{formatCurrency(total)}</h4>
        <div className="row g-2">
          {metrics.map((m, i) => <div className="col-4" key={i}>{m}</div>)}
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      {/* Header */}
      <div className="mb-4">
        <h4 className="fw-bold mb-1">Dashboard Overview</h4>
        <p className="text-muted small">Real-time overview of your property portfolio</p>
      </div>

      {/* Stats Row */}
      <div className="row g-3 mb-4">
        <StatCard label="Total Units" value={units.length} icon="building" color="text-primary" bgColor="bg-primary bg-opacity-10" />
        <StatCard label="Occupied Units" value={occupiedUnits.length} icon="home-check" color="text-success" bgColor="bg-success bg-opacity-10" />
        <StatCard label="Vacant Units" value={nonOccupiedUnits.length} icon="home-off" color="text-warning" bgColor="bg-warning bg-opacity-10" />
        <StatCard
          label="Net Position"
          value={formatCurrency(netPosition)}
          icon={netPosition >= 0 ? 'trending-up' : 'trending-down'}
          color={netPosition >= 0 ? 'text-success' : 'text-danger'}
          bgColor={netPosition >= 0 ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}
        />
      </div>

      {/* Finances Overview */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                  <h5 className="fw-bold mb-1">Finances Overview</h5>
                  <p className="text-muted small mb-0">Revenue, expense and wallet summary</p>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => navigate('/wallet')}
                  >
                    <i className="ti ti-wallet me-1"></i> View Wallet
                  </button>
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => setShowWithdrawModal(true)}
                    disabled={walletBalance <= 0}
                  >
                    <i className="ti ti-arrow-up me-1"></i> Withdraw
                  </button>
                </div>
              </div>


              <div className="row g-3">
                <FinanceCard
                  title="Revenue"
                  icon="coin"
                  color="primary"
                  total={revenueSummary.totalIncome}
                  metrics={[
                    <FinanceMetric key="paid" label="Paid" value={revenueSummary.totalPaid} color="success" bgColor="bg-success bg-opacity-10" />,
                    <FinanceMetric key="unpaid" label="Unpaid" value={(revenueSummary.totalIncome || 0) - (revenueSummary.totalPaid || 0)} color="warning" bgColor="bg-warning bg-opacity-10" />,
                    <FinanceMetric key="overdue" label="Overdue" value={revenueSummary.totalBalance} color="danger" bgColor="bg-danger bg-opacity-10" />
                  ]}
                  onClick={() => navigate("/portal/metrics/revenue_reports")}
                />
                <FinanceCard
                  title="Expenses"
                  icon="receipt"
                  color="secondary"
                  total={expenseSummary.totalToPay}
                  metrics={[
                    <FinanceMetric key="paid" label="Paid" value={expenseSummary.totalPaid} color="success" bgColor="bg-success bg-opacity-10" />,
                    <FinanceMetric key="unpaid" label="Unpaid" value={expenseSummary.totalUnpaid} color="warning" bgColor="bg-warning bg-opacity-10" />,
                    <FinanceMetric key="overdue" label="Overdue" value={expenseSummary.totalOverdue} color="danger" bgColor="bg-danger bg-opacity-10" />
                  ]}
                  onClick={() => navigate("/portal/metrics/expense_reports")}
                />
                <FinanceCard
                  title="Wallet"
                  icon="wallet"
                  color="success"
                  total={walletBalance}
                  metrics={[
                    <FinanceMetric
                      key="available"
                      label="Available"
                      value={walletBalance}
                      color="success"
                      bgColor="bg-success bg-opacity-10"
                    />,
                    <FinanceMetric
                      key="withdrawn"
                      label="Withdrawn"
                      value={recentWithdrawals}
                      color="secondary"
                      bgColor="bg-secondary bg-opacity-10"
                    />,
                    <FinanceMetric
                      key="transactions"
                      label="Transactions"
                      value={recentTransactions.length}
                      color="info"
                      bgColor="bg-info bg-opacity-10"
                    />
                  ]}
                  onClick={() => navigate("/wallet")}
                />
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Units Overview */}
      <div className="row">
        <div className="col-md-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              {/* Occupied Units */}
              <div className="mb-4">
                <SectionHeader
                  icon="home-check"
                  title="Occupied Units"
                  count={occupiedUnits.length}
                  color="success"
                  subtitle="Units currently occupied by residents"
                />
                <div className="row g-3">
                  {occupiedUnits.length > 0 ?
                    occupiedUnits.map((unit, i) => (
                      <UnitCard key={`occupied-${i}`} unit={unit} status="Occupied" borderColor="success" badgeColor={unit.customerType === "Home Owner" ? 'info' : 'warning'} />
                    ))
                    : <EmptyState icon="home-check" message="No occupied units" />
                  }
                </div>
              </div>

              {/* Non-Occupied Units */}
              <div>
                <SectionHeader
                  icon="home-off"
                  title="Non-Occupied Units"
                  count={nonOccupiedUnits.length}
                  color="warning"
                  subtitle="Available units ready for occupancy"
                />
                <div className="row g-3">
                  {nonOccupiedUnits.length > 0 ?
                    nonOccupiedUnits.map((unit, i) => (
                      <UnitCard key={`nonoccupied-${i}`} unit={unit} status="Vacant" borderColor="secondary" badgeColor={unit.customerType === "Home Owner" ? 'info' : 'warning'} />
                    ))
                    : <EmptyState icon="home-off" message="No vacant units" />
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Request Withdrawal</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowWithdrawModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Available Balance</label>
                  <h6 className="fw-bold text-success">{formatCurrency(walletBalance)}</h6>
                </div>
                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <div className="input-group">
                    <span className="input-group-text">KES</span>
                    <input
                      type="number"
                      className="form-control"
                      value={withdrawalAmount}
                      onChange={(e) => setWithdrawalAmount(e.target.value)}
                      placeholder="Enter amount"
                      min="0"
                      step="100"
                    />
                  </div>
                  <small className="text-muted">Minimum withdrawal: KES 100</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes (Optional)</label>
                  <textarea
                    className="form-control"
                    value={withdrawalNotes}
                    onChange={(e) => setWithdrawalNotes(e.target.value)}
                    placeholder="Add any notes for this withdrawal"
                    rows="2"
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowWithdrawModal(false)}
                  disabled={withdrawing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-warning"
                  onClick={handleWithdraw}
                  disabled={withdrawing || !withdrawalAmount || parseFloat(withdrawalAmount) <= 0}
                >
                  {withdrawing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-arrow-up me-1"></i> Submit Withdrawal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// Empty state component
const EmptyState = ({ icon, message }) => (
  <div className="col-12">
    <div className="text-center py-4 bg-light rounded-3">
      <i className={`ti ti-${icon} text-muted`} style={{ fontSize: '32px' }}></i>
      <p className="text-muted mt-2 mb-0">{message}</p>
    </div>
  </div>
);

export default Dashboard;